/* Minimal history router.

   Per the agreed state model: the *workspace* lives in the URL (/library),
   while search, chips, scroll and map viewport stay in memory only — so a
   refresh lands on a clean version of the same workspace (constitution §11 vs
   §12, resolved that way deliberately).

   Detail is the one thing layered on top of a workspace rather than replacing
   it: /item/42 means "item 42, over whatever workspace you were in". The
   workspace element stays mounted underneath, so Back restores it with its
   scroll, filters and map viewport intact for free (§10) — there is nothing to
   serialize because nothing was ever torn down.

   Two consequences of that layering, both deliberate:

   - The workspace under an item route is remembered in memory, not in the URL.
     A cold load of /item/42 therefore has no origin and falls back to the
     default workspace underneath, which is exactly §12.
   - Moving through a playlist REPLACES the item route instead of pushing. Back
     is "leave Detail", not "step back through everything I skimmed".

   Switching workspace is a plain push on every device. It was briefly a separate
   model on touch — one guard entry above the base, and "return to the base tab"
   as a single `history.back()` — and that model was unsound: sheets and overlays
   push entries above the base too, so the `back()` popped whichever layer
   happened to be on top instead of returning anywhere. Tapping Library landed on
   Map, or reopened the player. ADR 0021 has the measured stacks. Do not reinstate
   it here: per-tab history stacks are the native navigator's job, and this router
   has no way to tell its own entries from a component's. */

/** URL prefix every route sits under. Empty since the cutover — the app owns
    the origin, and the server's catch-all answers any document path. */
const BASE = '';

/**
 * Where a fresh session starts — the app root, which `start()` then normalises
 * to the default workspace.
 *
 * Exported so sign-out doesn't hardcode a path it would have to keep in sync
 * with `BASE`. The `|| '/'` is load-bearing now that `BASE` is empty:
 * `location.assign('')` reloads the current URL instead of going home.
 */
export const homeUrl = (): string => BASE || '/';

/** Where we are: a workspace, plus the item layered over it, if any. */
export interface Route {
  workspace: string;
  itemId: number | null;
}

/** Marks an entry we pushed for Detail. */
interface OverlayState { overlay?: boolean }

export class Router {
  private ids: string[] = [];
  private fallback = '';
  /** The workspace an item route sits on top of. Memory only — see above. */
  private workspace = '';

  private notify: (route: Route) => void = () => {};
  private onPop = () => {
    const route = this.parse();
    this.workspace = route.workspace;
    this.notify(route);
  };

  /** @param ids valid workspace ids, first one is the default */
  start(ids: string[], notify: (route: Route) => void): void {
    this.ids = ids;
    this.fallback = ids[0] ?? '';
    this.notify = notify;
    window.addEventListener('popstate', this.onPop);

    const route = this.parse();
    this.workspace = route.workspace;
    // Normalise / and anything unknown to the default workspace,
    // replacing rather than pushing so Back doesn't land on a redirect. An item
    // route is left exactly as it is — it is a legitimate deep link.
    if (route.itemId === null && this.head() !== route.workspace) {
      history.replaceState({}, '', `${BASE}/${route.workspace}`);
    }
    this.notify(route);
  }

  stop(): void {
    window.removeEventListener('popstate', this.onPop);
  }

  private parts(): string[] {
    return location.pathname.slice(BASE.length).replace(/^\/+|\/+$/g, '').split('/');
  }

  private head(): string {
    return this.parts()[0] ?? '';
  }

  private parse(): Route {
    const [head, tail] = this.parts();
    if (head === 'item') {
      const id = Number(tail);
      return {
        workspace: this.workspace || this.fallback,
        itemId: Number.isInteger(id) && id > 0 ? id : null,
      };
    }
    return {
      workspace: this.ids.includes(head) ? head : this.fallback,
      itemId: null,
    };
  }

  /** Active workspace id, falling back to the default for unknown paths. */
  current(): string {
    return this.parse().workspace;
  }

  /** True when the current entry is one we pushed for Detail. */
  private onItem(): boolean {
    return (history.state as OverlayState | null)?.overlay === true;
  }

  /**
   * True when the current entry carries no marker — a plain workspace entry that
   * `Router` wrote itself, and so the only kind it may overwrite.
   *
   * A marked entry belongs to a layer: a sheet, the search band, an action sheet.
   * Its owner is holding a flag for that entry and looks for it again on the way
   * out, so overwriting it leaves the owner pointing at something that no longer
   * exists and its sheet can no longer be dismissed with Back.
   *
   * Detail's own entry is the exception, handled separately in `go`: it is
   * overwritten deliberately, and safely, because closing Detail unmounts
   * `g-detail` — a fresh instance starts with no flag to be stale. A workspace
   * stays mounted (0002 §*Workspaces mount lazily and then STAY mounted*), which
   * is exactly why its layers cannot be treated the same way.
   */
  private onPlainEntry(): boolean {
    const state = history.state as Record<string, unknown> | null;
    return !state || Object.keys(state).length === 0;
  }

  /**
   * Navigate to a workspace. No-op when already there, so the rail can't stack
   * history.
   *
   * Leaving Detail this way **replaces** the item entry instead of pushing on
   * top of it, which is both a design point and a bug fix.
   *
   * The design point: the overlay was layered over a workspace, so stepping
   * sideways out of it should leave Back meaning "the workspace I started from",
   * not "the item I was just looking at".
   *
   * The bug: the first version closed and then navigated — `closeItem()`
   * followed by `go()`. `closeItem` traverses history, which is *asynchronous*,
   * so the deferred traversal landed after the push and silently undid it.
   * Detail closed and the app stayed exactly where it was. Anything that leaves
   * Detail for somewhere else has to be one history operation, not two.
   *
   * Switching workspace **replaces** rather than pushes, so the stack does not
   * grow with use — otherwise leaving the app costs one Back press per navigation
   * you made, which on a phone is the whole session. Back therefore means "close
   * what is open", and once nothing is open it leaves the app. It never means
   * "the tab before this one"; see ADR 0021 decision 3.
   *
   * The push branch is not the tab case. It fires only when the current entry
   * belongs to a layer that is still open — tapping a tab with Map's venue sheet
   * up, say — where overwriting would strand that sheet's owner. That costs one
   * entry per open layer, which is bounded by what is on screen rather than by
   * how long the session has run.
   */
  go(id: string): void {
    const route = this.parse();
    if (route.workspace === id && route.itemId === null) return;
    const url = `${BASE}/${id}`;

    if (this.onItem() || this.onPlainEntry()) history.replaceState({}, '', url);
    else history.pushState({}, '', url);
    this.workspace = id;
    this.notify({ workspace: id, itemId: null });
  }

  /**
   * Layer Detail over the current workspace.
   *
   * Pushes the first time and replaces while already open, which is what makes
   * Back mean "close Detail" however far through a playlist you have moved.
   */
  openItem(id: number): void {
    const state = { overlay: true };
    const url = `${BASE}/item/${id}`;
    if (this.onItem()) history.replaceState(state, '', url);
    else history.pushState(state, '', url);
  }

  /**
   * Close Detail and return to the workspace underneath.
   *
   * `history.back()` when we pushed the entry ourselves, so the forward/back
   * stack stays honest and popstate does the notifying. On a cold deep link
   * there is no entry of ours to pop and going back would leave the app
   * entirely, so that case rewrites the URL in place instead.
   */
  closeItem(): void {
    if (this.onItem()) {
      history.back();
      return;
    }
    history.replaceState({}, '', `${BASE}/${this.workspace}`);
    this.notify({ workspace: this.workspace, itemId: null });
  }
}
